import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';
import { Table, Row, Col, Breadcrumb, Button, Typography, notification } from 'antd';

import {
  intialPaginationState,
  showingPageTotalRange,
  unexpectedError,
  PictureCell,
  onlyYears,
  onlyYear,
  filter,
} from '../../../common_functions';

const PublishedImage = props => {
  const { id: reportType } = useParams();

  const [reports, setReports] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]); // For checkbox selection
  const [pagination, setPagination] = useState(intialPaginationState);
  const [sorter, setSorter] = useState({});
  const [loading, setLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});

  useEffect(() => {
    getPublishedImages(pagination);
  }, []);

  // Helper to build filter params for API using Directus nested notation
  const buildApiFilters = filters => {
    const apiFilters = {};
    if (filters['negative.obj_name'] && filters['negative.obj_name'].length > 0) {
      apiFilters.negative = { obj_name: { _icontains: filters['negative.obj_name'][0] } };
    }
    if (filters['books.book_title'] && filters['books.book_title'].length > 0) {
      apiFilters.books = { ...apiFilters.books, book_title: { _icontains: filters['books.book_title'][0] } };
    }
    if (filters['books.date_of_publication'] && filters['books.date_of_publication'].length > 0) {
      const year = filters['books.date_of_publication'][0]?.trim();
      if (/^\d{4}$/.test(year)) {
        apiFilters.books = { ...apiFilters.books, date_of_publication: { _between: [`${year}-01-01`, `${year}-12-31`] } };
      }
    }
    return apiFilters;
  };

  const getPublishedImages = async (paginationObj, sorterObj = sorter, filters = appliedFilters) => {
    try {
      setLoading(true);

      let sortParam = '';

      if (sorterObj?.field) {
        const apiField = sortFieldMap[sorterObj.field] || sorterObj.field;
        sortParam = sorterObj.order === 'ascend' ? apiField : '-' + apiField;
      }

      const apiFilters = buildApiFilters(filters);

      const resp = await props.earliestIllustrations({
        limit: paginationObj.pageSize,
        offset: paginationObj.pageSize * (paginationObj.current - 1),
        sort: sortParam,
        filters: apiFilters, // Use 'filters' to match other reports
      });

      setReports(resp.data || []);

      setPagination({
        ...paginationObj,
        total: resp.meta?.total || 0,
      });
    } catch (error) {
      console.error(error);
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = async (paginationObj, filters, sorterObj) => {
    setPagination(paginationObj);
    setSorter(sorterObj);
    setAppliedFilters(filters);
    await getPublishedImages(paginationObj, sorterObj, filters);
  };
  const sortFieldMap = {
    image_title: 'image_title',
    book_title: 'book_title',
    date_of_publication: 'date_of_publication',
  };
  const columns = [
    {
      title: 'First Pub Image',
      key: 'plate_image',
      render: (_, record) => (record?.image ? <PictureCell dataIndexValue={record.image} getFile={props.getFile} width={50} /> : '-'),
    },
    {
      title: 'Image Title',
      dataIndex: 'image_title',
      key: 'image_title',
      sorter: true,
      render: value => value || '-',
    },
    {
      title: 'First Pub Title',
      dataIndex: ['books', 'book_title'],
      key: 'books.book_title',
      ...filter('books.book_title'),
      render: (_, record) => record?.books?.book_title || '-',
    },
    {
      title: 'Negative #',
      dataIndex: ['negative', 'obj_name'],
      key: 'negative.obj_name',
      ...filter('negative.obj_name'),
      render: (_, record) => record?.negative?.obj_name || '-',
    },
    {
      title: 'Negative Date',
      key: 'negative',
      render: (_, record) => onlyYears(record?.negative?.creation_earliest, record?.negative?.creation_latest) || '-',
    },
    {
      title: 'Plate #',
      dataIndex: 'plate',
      key: 'plate',
      width: 100,
      render: value => value || '-',
    },
    {
      title: 'First Pub Date',
      dataIndex: ['books', 'date_of_publication'],
      key: 'books.date_of_publication',
      ...filter('books.date_of_publication'),
      sorter: true,
      render: (_, record) => onlyYear(record?.books?.date_of_publication) || '-',
    },
    {
      title: 'No. of Pubs',
      dataIndex: 'number_of_publications',
      key: 'number_of_publications',
      width: 120,
      render: value => value || '-',
    },
  ];

  const downloadCSV = async e => {
    // If any rows are selected, ask user if they want to download selected or all
    if (selectedRowKeys.length > 0) {
      // Use Ant Design Modal for a better UX
      const { Modal } = require('antd');
      Modal.confirm({
        title: 'Download CSV',
        content: 'You have selected records. Do you want to download only the selected records or all records?',
        okText: 'Selected Only',
        cancelText: 'All Records',
        cancelButtonProps: {
          style: {
            backgroundColor: '#a6a300',
            borderColor: '#a6a300',
            color: '#fff',
          },
        },
        onOk: () => {
          // Download only selected records
          const selectedData = reports.filter(item => selectedRowKeys.includes(item.id));
          if (!selectedData.length) {
            notification.warning({ message: 'No selected data found on this page.' });
            return;
          }
          const csvData = selectedData.map(item => ({
            Image_Title: item.image_title || '',
            Book_Title: item?.books?.book_title || '',
            Negative: item?.negative?.obj_name || '',
            Publication_Date: onlyYear(item?.books?.date_of_publication) || '',
            Plate: item.plate || '',
            Publications: item.number_of_publications || '',
          }));
          const headers = Object.keys(csvData[0]).join(',');
          const rows = csvData.map(row =>
            Object.values(row)
              .map(value => `"${String(value).replace(/"/g, '""')}"`)
              .join(',')
          );
          const csvContent = [headers, ...rows].join('\n');
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'published_images_selected.csv');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        onCancel: () => {
          // Download all data as CSV (existing logic)
          downloadAllCSV();
        },
      });
      return;
    }
    // Download all data as CSV (existing logic)
    await downloadAllCSV();
  };

  // Helper to download all data as CSV
  const downloadAllCSV = async () => {
    try {
      setLoading(true);
      const apiFilters = buildApiFilters(appliedFilters);
      const resp = await props.earliestIllustrations({
        limit: pagination?.total,
        sort: sorter?.field ? (sorter.order === 'ascend' ? sorter.field : '-' + sorter.field) : '',
        filters: apiFilters,
      });
      const allData = resp.data;
      if (!allData || !allData.length) {
        notification.warning({ message: 'No data available to download' });
        return;
      }
      const csvData = allData.map(item => ({
        Image_Title: item.image_title || '',
        Book_Title: item?.books?.book_title || '',
        Negative: item?.negative?.obj_name || '',
        Publication_Date: onlyYear(item?.books?.date_of_publication) || '',
        Plate: item.plate || '',
        Publications: item.number_of_publications || '',
      }));
      const headers = Object.keys(csvData[0]).join(',');
      const rows = csvData.map(row =>
        Object.values(row)
          .map(value => `"${String(value).replace(/"/g, '""')}"`)
          .join(',')
      );
      const csvContent = [headers, ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'published_images_report.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
      notification.error({ message: 'Failed to download report' });
    } finally {
      setLoading(false);
    }
  };

  // Download all data as CSV (existing logic)
  // (removed duplicate block)

  // Download only selected records as CSV (legacy, now handled in downloadCSV)
  // const downloadSelectedCSV = () => {};

  const generatePDF = async () => {
    if (selectedRowKeys.length === 0) {
      notification.warning({ message: 'Please select at least one record.' });
      return;
    }
    if (selectedRowKeys.length > 10) {
      notification.warning({ message: 'You can select a maximum of 10 records.' });
      return;
    }
    try {
      setLoading(true);
      await props.PublishedImagePdfReport({ selectedIds: selectedRowKeys });
      setSelectedRowKeys([]);
      notification.success({
        message: 'Report Queued',
        description: 'Your report is being generated. You will be notified by email when it is ready. Download it from Report History.',
        duration: 6,
      });
    } catch (error) {
      notification.error({ message: 'Failed to queue report. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // End of component logic

  return (
    <Row className={loading ? 'disabledComponent' : ''}>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Reports", href: `/reports/bibliography-report/${reportType}` }, { title: "Published Images" }]} />
      </Col>

      <Col className="white-paper" span={24}>
        <Row justify="space-between" align="middle">
          <Col xs={12}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Images Published by Ansel Adams
            </Typography.Title>
          </Col>
          <Col xs={12} justify="end" align="right">
            <Button
              style={{ marginRight: '8px' }}
              icon={<FilePdfOutlined />}
              type="primary"
              size="small"
              onClick={e => generatePDF()}
              disabled={selectedRowKeys.length === 0 || selectedRowKeys.length > 20}
            >
              Generate Pdf
            </Button>
            <Button
              icon={<DownloadOutlined />}
              type="primary"
              size="small"
              onClick={downloadCSV}
              style={{ backgroundColor: '#a6a300', borderColor: '#a6a300', color: '#fff' }}
            >
              Download CSV
            </Button>
          </Col>
        </Row>

        <div style={{ marginTop: 20 }}>
          <Table
            className="no-fixed-last-col"
            scroll={{ x: 'max-content' }}
            columns={columns}
            bordered
            dataSource={reports}
            pagination={{
              ...pagination,
              ...showingPageTotalRange,
            }}
            loading={loading}
            onChange={handleTableChange}
            rowKey="id"
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys,
              onChange: (newSelectedKeys, selectedRows) => {
                // Merge previous selections with new ones from current page
                let mergedKeys = [...selectedRowKeys];
                // Remove any keys from current page
                const currentPageIds = reports.map(r => r.id);
                mergedKeys = mergedKeys.filter(id => !currentPageIds.includes(id));
                // Add new selections from current page
                mergedKeys = [...mergedKeys, ...newSelectedKeys];
                // Enforce max 20
                if (mergedKeys.length <= 20) {
                  setSelectedRowKeys(mergedKeys);
                } else {
                  notification.warning({ message: 'You can select a maximum of 20 records.' });
                }
              },
              getCheckboxProps: record => ({
                disabled: selectedRowKeys.length >= 20 && !selectedRowKeys.includes(record.id),
                checked: selectedRowKeys.includes(record.id),
              }),
            }}
          />
        </div>
      </Col>
    </Row>
  );
};
export default PublishedImage;
